import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import {
  type Permission, type StatusNote, PERMISSION_LABEL, PERMISSION_STYLE,
  STATUS_NOTE_LABEL, STATUS_NOTE_SHORT, STATUS_NOTE_STYLE, prettyUse,
} from '../lib/zoningLabels';

/**
 * ZS2 - the zoning rule book, on the parcel it is about.
 *
 * 1,122 signed rules went live on 2026-09-04 and, until this panel existed, `lookupRule` had
 * exactly one caller (`GET /zoning-rules/lookup`) and the frontend never called it. The rules
 * were browsable on the admin screen at `/zoning-rules` and answered nothing anywhere a broker
 * actually stands - which is this repo's dominant defect class, an endpoint with no caller.
 *
 * ⭐ WHAT JOSEPH ASKED FOR, 2026-09-04: *"if it's a property where the zoning is, like, 'ask
 * planning for a written interpretation', then we know to do that."* He described the HELD-rule
 * display, and it is the more valuable half of this panel - not the answers, the QUESTIONS.
 *
 * ⛔ A HELD RULE SHOWS ITS QUESTION AND NEVER ITS ANSWER. The redaction is done on the SERVER
 * (`lookupParcel`) - `permission` and `conditions` are not in the payload for a held rule, so
 * there is nothing here to render by accident. This component could not leak them if it tried,
 * which is the point: the gate is not a UI convention.
 *
 * ⚠️ THREE STATES, KEPT DISTINCT. Collapsing any two hides what to do next:
 *   approved        the rule.               Use it.
 *   pending_review  the question.           Call Planning - and do NOT re-research it.
 *   none            nobody has looked.      Research it.
 */

interface Rule {
  id: string;
  use_case: string;
  permission: Permission;
  conditions: string | null;
  notes: string | null;
  code_section: string | null;
  source_url: string | null;
  jurisdiction: string | null;
  zone_name: string | null;
  status_note: StatusNote | null;
  is_stale: boolean;
  verified_at: string | null;
}

interface Entry {
  use_case: string;
  status: 'none' | 'pending_review' | 'approved';
  rule: Rule | null;
  held: { use_case: string; status_note: string | null } | null;
}

/**
 * Sort order is ACTIONABILITY, not the use-case list's order.
 *
 * ⚠️ Measured, not assumed: 616 of the 1,122 answering rules say `unknown` - 55% of them. In
 * natural order the two or three rows a broker is actually looking for sit buried among a wall
 * of "researched, unclear". The plays float, the questions follow, the dead ends sink.
 */
const rank = (e: Entry): number => {
  if (e.status === 'approved') {
    const p = e.rule!.permission;
    return p === 'by_right' ? 0 : p === 'cup' ? 1 : p === 'unknown' ? 3 : 4;
  }
  return e.status === 'pending_review' ? 2 : 5;
};

/**
 * `conditions` is a RESEARCH NARRATIVE, not a one-line caveat. Measured on the live table
 * 2026-09-04: mean length 1,956 characters, 1,075 of 1,204 rules over 600, longest 10,160 - they
 * carry the code citations, the supremacy chain and the provenance of every inference. Rendered
 * inline that is a wall of text per row and the panel cannot be scanned at all.
 *
 * ⚠️ TRUNCATING A CAVEAT IS ONLY SAFE BECAUSE THE WARNING IS NOT IN THIS TEXT. The category of
 * the caveat rides on `status_note`, a fixed vocabulary shown as its own pill and never
 * shortened - "⚠ Standard may bar" is what stops a by-right being taken to an owner. This text
 * is the detail behind that pill, so it can fold. It is one click away, never hidden.
 */
const LEAD_CHARS = 180;
const isLong = (c: string) => c.length > LEAD_CHARS;
const lead = (c: string) => {
  if (!isLong(c)) return c;
  const cut = c.slice(0, LEAD_CHARS);
  const stop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('; '));
  return (stop > 80 ? cut.slice(0, stop + 1) : cut.replace(/\s+\S*$/, '')) + ' ...';
};

/**
 * `mapped` (FZ-FIX2 J2, the 150th; his call "Hide them until mapped"): the zone was read from the city's MAP - only then
 * does the server answer the nine building uses (warehouse, retail, restaurant...). CoStar's text answers the yard uses only.
 */
export default function ZoningPanel({ city, zoning, mapped = false }: { city?: string | null; zoning?: string | null; mapped?: boolean }) {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [flagging, setFlagging] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  /**
   * PG1, HIS ASK 2026-09-04: *"can we maybe make it to where it's an accordion style, just to make
   * it shorter so it's not eating up a lot of the page."* Seven use cases render as seven rows.
   *
   * ⛔⛔ COLLAPSED BY DEFAULT, BUT THE SUMMARY ROW KEEPS THE SIGNAL. ZS2's whole point is that a
   * HELD rule surfaces its QUESTION on the property, so he calls ONE city about ONE parcel he
   * cares about - collapsing that out of sight would quietly undo the feature this is tidying.
   * The "N of M researched / N awaiting a question" line is visible in BOTH states, and it is the
   * held count that tells him there is something to open.
   */
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    if (!city) return;
    try {
      const r = await api.lookupParcelZoning(city, zoning, mapped);
      setEntries(r.entries as Entry[]);
      setCode(r.code);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the zoning rules');
    }
  }, [city, zoning, mapped]);

  useEffect(() => { void load(); }, [load]);

  const submitFlag = async (id: string) => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.flagZoningRule(id, note.trim());
      setFlagging(null);
      setNote('');
      // Refetch rather than patch in place: flagging returns the rule to draft, so the row must
      // switch from an answer to a question - which is the server's call to make, not ours.
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not flag the rule');
    } finally {
      setBusy(false);
    }
  };

  // Nothing to say about a parcel with no city on file, and no honest way to guess one - the
  // rules are keyed on (city, code) because M1 in Fontana is not M1 in Riverside.
  if (!city) return null;
  if (error) return <p className="mt-[18px] border-t border-gray-150 pt-[18px] text-[12.5px] text-gray-500">{error}</p>;
  if (!entries) return null;

  const sorted = [...entries].sort((a, b) => rank(a) - rank(b));
  const answered = entries.filter((e) => e.status === 'approved').length;
  const held = entries.filter((e) => e.status === 'pending_review').length;

  return (
    <div className="mt-[18px] border-t border-gray-150 pt-[18px]">
      {/* The summary row IS the toggle - the same idiom as the transaction cards on this page. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        disabled={!code}
        className={`mb-1 flex w-full flex-wrap items-baseline gap-x-2.5 gap-y-1 text-left ${
          code ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        <div className="text-[9.5px] font-bold uppercase leading-none tracking-[.14em] text-primary-500">
          What the zoning allows
        </div>
        <span className="text-[11.5px] text-gray-400">
          {code
            ? <>{city} {code} &middot; {answered} of {entries.length} researched{held > 0 && <> &middot; {held} awaiting a question</>}</>
            : <>No zoning code on file for this parcel</>}
        </span>
        {code && (
          <span className="ml-auto flex items-center gap-1 text-[11.5px] font-semibold text-gray-500">
            {expanded ? 'Hide' : 'Show'}
            <svg
              viewBox="0 0 20 20" aria-hidden="true"
              className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" strokeWidth="2"
            >
              <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
      </button>

      {!code && (
        <p className="mt-2.5 text-[12.5px] text-gray-500">
          Add a zoning code above and the rule book will answer for it.
        </p>
      )}

      {code && expanded && (
        <ul className="mt-3 divide-y divide-gray-150">
          {sorted.map((e) => (
            <li key={e.use_case} className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1.5 py-2.5">
              <span className="w-40 shrink-0 text-[13px] font-medium text-gray-800">{prettyUse(e.use_case)}</span>

              {/* ── Signed: the answer ──────────────────────────────────────────── */}
              {e.status === 'approved' && e.rule && (
                <>
                  <span className={`rounded border px-2 py-0.5 text-[11px] ${PERMISSION_STYLE[e.rule.permission]}`}>
                    {PERMISSION_LABEL[e.rule.permission]}
                  </span>
                  {e.rule.status_note && (
                    <span
                      title={STATUS_NOTE_LABEL[e.rule.status_note]}
                      className={`rounded border px-2 py-0.5 text-[11px] ${STATUS_NOTE_STYLE[e.rule.status_note]}`}
                    >
                      {STATUS_NOTE_SHORT[e.rule.status_note]}
                    </span>
                  )}
                  {e.rule.is_stale && (
                    <span className="rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
                      Verified over a year ago
                    </span>
                  )}
                  {e.rule.conditions && (
                    <div className="min-w-0 basis-full text-[12px] leading-snug text-gray-600">
                      {open === e.rule.id ? (
                        <p className="whitespace-pre-wrap">{e.rule.conditions}</p>
                      ) : (
                        <p>{lead(e.rule.conditions)}</p>
                      )}
                      {isLong(e.rule.conditions) && (
                        <button
                          type="button"
                          onClick={() => setOpen(open === e.rule!.id ? null : e.rule!.id)}
                          className="mt-1 text-[11.5px] font-medium text-primary-500 underline-offset-2 hover:underline"
                        >
                          {open === e.rule.id ? 'Show less' : 'Read the full finding'}
                        </button>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => { setFlagging(flagging === e.rule!.id ? null : e.rule!.id); setNote(''); }}
                    className="ml-auto shrink-0 text-[11.5px] font-medium text-gray-400 underline-offset-2 hover:text-primary-500 hover:underline"
                  >
                    {flagging === e.rule.id ? 'Cancel' : 'This looks wrong'}
                  </button>
                </>
              )}

              {/* ── Held: the QUESTION, never the answer ───────────────────────────
                  The server sends no permission for these, so there is none to show. */}
              {e.status === 'pending_review' && (
                <>
                  <span className="rounded border border-info/30 bg-info-wash px-2 py-0.5 text-[11px] text-info">
                    Ask Planning
                  </span>
                  {/* Its own line on a phone (the 151st's look: at 390 px the name and the pill left it ~50 px and long
                      words were cut off); beside the pill from sm up. */}
                  <span className="min-w-0 basis-full text-[12px] leading-snug text-gray-600 sm:basis-0 sm:flex-1">
                    {e.held?.status_note
                      ? STATUS_NOTE_LABEL[e.held.status_note as StatusNote]
                      : 'Researched, held for a question - not yet signed off.'}
                  </span>
                </>
              )}

              {/* ── Unresearched. A gap is a finding, so it is shown, not omitted. ── */}
              {e.status === 'none' && (
                <span className="text-[12px] text-gray-400">Not researched for this zone yet</span>
              )}

              {/* The flag note. A flag with no reason is refused by the API - the note IS the
                  deliverable, because it is what the next person reads instead of the rule. */}
              {e.rule && flagging === e.rule.id && (
                <div className="basis-full pt-2">
                  <textarea
                    autoFocus
                    rows={2}
                    value={note}
                    onChange={(ev) => setNote(ev.target.value)}
                    placeholder="What looks wrong? e.g. the city told me outside storage needs a CUP here."
                    className="w-full rounded-lg border border-gray-300 px-2.5 py-2 text-[12.5px] text-gray-800 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none"
                  />
                  <div className="mt-1.5 flex items-center gap-2.5">
                    <button
                      type="button"
                      disabled={busy || !note.trim()}
                      onClick={() => void submitFlag(e.rule!.id)}
                      className="rounded-lg bg-primary-500 px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-40"
                    >
                      {busy ? 'Withdrawing...' : 'Withdraw this rule'}
                    </button>
                    <span className="text-[11.5px] text-gray-500">
                      It stops answering everywhere until someone re-signs it.
                    </span>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
